"use client";

import { Button } from "@oreme/ui";
import { useAtom, useAtomValue } from "jotai";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { type ReactElement, useEffect } from "react";
import {
  SitesPanelLegend,
  SitesThemeList,
  SitesSidebarFilterTag,
  SitesSidebarFilterLocation,
  SitesSidebarFilterObservatory,
} from "@/components";
import { sitesCountersAtom, sidebarVisibilityAtom } from "@/store/sites";
import { ThemeLegend, ThemeTypeEnum } from "@/types";
import { useIsMobile } from "@oreme/ui/hooks/use-mobile";

function SitesSidebar(): ReactElement {
  const t = {
    theme: useTranslations("global.Theme"),
    sidebar: useTranslations("sites.Sidebar"),
  };

  const [isSidebarVisible, toggleSidebarVisibility] = useAtom(
    sidebarVisibilityAtom,
  );
  const counters = useAtomValue(sitesCountersAtom);
  const isMobile = useIsMobile();

  const themes: ThemeLegend[] = [
    {
      label: t.theme("earth"),
      type: ThemeTypeEnum.EARTH,
      count: counters.byTheme[ThemeTypeEnum.EARTH],
    },
    {
      label: t.theme("water"),
      type: ThemeTypeEnum.WATER,
      count: counters.byTheme[ThemeTypeEnum.WATER],
    },
    {
      label: t.theme("universe"),
      type: ThemeTypeEnum.UNIVERSE,
      count: counters.byTheme[ThemeTypeEnum.UNIVERSE],
    },
    {
      label: t.theme("biodiversity"),
      type: ThemeTypeEnum.BIODIVERSITY,
      count: counters.byTheme[ThemeTypeEnum.BIODIVERSITY],
    },
  ];

  useEffect(() => {
    toggleSidebarVisibility(!isMobile);
  }, [isMobile, toggleSidebarVisibility]);

  if (isSidebarVisible) {
    return (
      <aside className="absolute z-20 top-0 left-0 bottom-0 w-96 p-3 pointer-events-none">
        <div className="relative w-full max-h-full border rounded-xl shadow-md bg-background pointer-events-auto flex flex-col min-h-0 overflow-hidden">
          <div className="overflow-y-auto min-h-0">
            <header className="relative flex flex-row gap-2 items-center justify-between p-3 m-0">
              <div className="flex flex-row items-center">
                <span className="text-lg">{counters.total}</span>
                &nbsp;
                {t.sidebar("siteCounter", { count: counters.total })}
              </div>
              <Button
                className="after:absolute after:inset-0"
                onClick={() => {
                  toggleSidebarVisibility();
                }}
                variant="ghost"
                size="icon"
              >
                {isSidebarVisible ? (
                  <>
                    <PanelLeftClose size={20} strokeWidth={1} />
                    <span className="sr-only">{t.sidebar("close")}</span>
                  </>
                ) : (
                  <>
                    <PanelLeftOpen size={20} strokeWidth={1} />
                    <span className="sr-only">{t.sidebar("open")}</span>
                  </>
                )}
              </Button>
            </header>
            <div className="p-3 m-0 flex flex-col gap-6">
              <div className="p-0 m-0">
                <SitesThemeList themes={themes} />
              </div>
              <div className="flex flex-col gap-1.5 p-0 m-0">
                <h4 className="uppercase text-xs font-semibold text-foreground! p-0 h-auto mb-1">
                  {t.sidebar("search")}
                </h4>
                <SitesSidebarFilterTag />
                <SitesSidebarFilterObservatory />
                <SitesSidebarFilterLocation />
              </div>
              <footer className="p-0 m-0">
                <SitesPanelLegend />
              </footer>
            </div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="absolute z-20 top-3 left-3 w-90 p-3 flex flex-col gap-4 rounded-xl bg-background/90 border border-opacity-40 hover:bg-background hover:border-opacity-100 transition-background animate__animated animate__fadeIn animate__faster">
      <header className="relative flex flex-row gap-2 items-center justify-between">
        <div className="flex flex-row items-center">
          <span className="text-lg">{counters.total}</span>
          &nbsp;
          {t.sidebar("siteCounter", { count: counters.total })}
        </div>
        <Button
          className="after:absolute after:inset-0"
          onClick={() => {
            toggleSidebarVisibility();
          }}
          variant="ghost"
          size="icon"
        >
          {isSidebarVisible ? (
            <>
              <PanelLeftClose size={20} strokeWidth={1} />
              <span className="sr-only">{t.sidebar("close")}</span>
            </>
          ) : (
            <>
              <PanelLeftOpen size={20} strokeWidth={1} />
              <span className="sr-only">{t.sidebar("open")}</span>
            </>
          )}
        </Button>
      </header>
    </aside>
  );
}

export { SitesSidebar };
